import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { Button, Modal, Pagination, Tabs } from '../components/ui';
import PageHeader from '../components/ui/PageHeader';
import CampaignFilters from '../components/campaigns/CampaignFilters';
import CampaignForm from '../components/campaigns/CampaignForm';
import CampaignList from '../components/campaigns/CampaignList';
import CampaignFollowFilters from '../components/campaign-follows/CampaignFollowFilters';
import CampaignFollowForm from '../components/campaign-follows/CampaignFollowForm';
import CampaignFollowList from '../components/campaign-follows/CampaignFollowList';
import { campaignService } from '../services/campaign.service';
import { campaignFollowService } from '../services/campaignFollow.service';
import { messageHandler } from '../services';
import type { Campaign, CampaignFilters as Filters } from '../types/campaign';
import type { CampaignFollow, CampaignFollowFilters as FollowFilters } from '../types/campaignFollow';

function Campaigns() {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('campaigns');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(null);
  const [selectedFollowRule, setSelectedFollowRule] = useState<CampaignFollow | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [followPage, setFollowPage] = useState(1);
  const [filters, setFilters] = useState<Filters>({});
  const [followFilters, setFollowFilters] = useState<FollowFilters>({});

  const { data, isLoading, error } = useQuery({
    queryKey: ['campaigns', currentPage, filters],
    queryFn: () => campaignService.getAll(currentPage, 20, filters),
    retry: 1,
  });

  const { data: followData, isLoading: followLoading, error: followError } = useQuery({
    queryKey: ['campaign-follows', followPage, followFilters],
    queryFn: () => campaignFollowService.getAll(followPage, 20, followFilters),
    retry: 1,
  });

  const createMutation = useMutation({
    mutationFn: campaignService.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      setIsModalOpen(false);
      messageHandler.success('Campanha criada com sucesso');
    },
    onError: (error: any) => {
      messageHandler.error(error.response?.data?.error || 'Erro ao criar campanha');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) =>
      campaignService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      setIsModalOpen(false);
      setSelectedCampaign(null);
      messageHandler.success('Campanha atualizada com sucesso');
    },
    onError: (error: any) => {
      messageHandler.error(error.response?.data?.error || 'Erro ao atualizar campanha');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: campaignService.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      messageHandler.success('Campanha excluída com sucesso');
    },
    onError: (error: any) => {
      messageHandler.error(error.response?.data?.error || 'Erro ao excluir campanha');
    },
  });

  const createFollowMutation = useMutation({
    mutationFn: campaignFollowService.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaign-follows'] });
      setIsModalOpen(false);
      messageHandler.success('Regra de follow-up criada com sucesso');
    },
    onError: (error: any) => {
      messageHandler.error(error.response?.data?.error || 'Erro ao criar regra de follow-up');
    },
  });

  const updateFollowMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) =>
      campaignFollowService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaign-follows'] });
      setIsModalOpen(false);
      setSelectedFollowRule(null);
      messageHandler.success('Regra de follow-up atualizada com sucesso');
    },
    onError: (error: any) => {
      messageHandler.error(error.response?.data?.error || 'Erro ao atualizar regra de follow-up');
    },
  });

  const deleteFollowMutation = useMutation({
    mutationFn: campaignFollowService.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaign-follows'] });
      messageHandler.success('Regra de follow-up excluída com sucesso');
    },
    onError: (error: any) => {
      messageHandler.error(error.response?.data?.error || 'Erro ao excluir regra de follow-up');
    },
  });

  const toggleFollowActiveMutation = useMutation({
    mutationFn: ({ id, is_active }: { id: number; is_active: boolean }) =>
      campaignFollowService.toggleActive(id, { is_active }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaign-follows'] });
      messageHandler.success('Status alterado com sucesso');
    },
    onError: (error: any) => {
      messageHandler.error(error.response?.data?.error || 'Erro ao alterar status');
    },
  });

  const handleOpenModal = (campaign?: Campaign) => {
    setSelectedCampaign(campaign || null);
    setSelectedFollowRule(null);
    setIsModalOpen(true);
  };

  const handleOpenFollowModal = (followRule?: CampaignFollow) => {
    setSelectedFollowRule(followRule || null);
    setSelectedCampaign(null);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedCampaign(null);
    setSelectedFollowRule(null);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);

    const filterLinkedRep = formData.get('filter_linked_representative') as string;
    const filterLinkedGroup = formData.get('filter_linked_lead_group') as string;
    const filterNoServices = formData.get('filter_no_services') as string;
    const filterWithService = formData.get('filter_with_service') as string;

    const parseServiceIds = (value: string): number[] | undefined => {
      if (!value || value.trim() === '') return undefined;
      return value
        .split(',')
        .map(id => parseInt(id.trim()))
        .filter(id => !isNaN(id));
    };

    const campaignData = {
      name: formData.get('name') as string,
      description: formData.get('description') as string || undefined,
      start_date: formData.get('start_date') as string,
      end_date: formData.get('end_date') as string,
      daily_limit: formData.get('daily_limit')
        ? parseInt(formData.get('daily_limit') as string)
        : undefined,
      status: formData.get('status') as any,
      template_id: parseInt(formData.get('template_id') as string),
      whatsapp_id: formData.get('whatsapp_id')
        ? parseInt(formData.get('whatsapp_id') as string)
        : undefined,
      assistant_id: formData.get('assistant_id')
        ? parseInt(formData.get('assistant_id') as string)
        : undefined,
      queue_id: parseInt(formData.get('queue_id') as string),
      time_to_send: formData.get('time_to_send') as string || undefined,
      filter_linked_representative: filterLinkedRep === '' ? null : filterLinkedRep === 'true',
      filter_representative_id: formData.get('filter_representative_id')
        ? parseInt(formData.get('filter_representative_id') as string)
        : undefined,
      filter_linked_lead_group: filterLinkedGroup === '' ? null : filterLinkedGroup === 'true',
      filter_lead_group_id: formData.get('filter_lead_group_id')
        ? parseInt(formData.get('filter_lead_group_id') as string)
        : undefined,
      filter_last_contact_days: formData.get('filter_last_contact_days')
        ? parseInt(formData.get('filter_last_contact_days') as string)
        : undefined,
      filter_origin_lead: formData.get('filter_origin_lead') as string || undefined,
      filter_type: formData.get('filter_type') as string || undefined,
      filter_no_services: parseServiceIds(filterNoServices),
      filter_with_service: parseServiceIds(filterWithService),
    };

    if (selectedCampaign) {
      updateMutation.mutate({ id: selectedCampaign.id, data: campaignData });
    } else {
      createMutation.mutate(campaignData);
    }
  };

  const handleDelete = (campaign: Campaign) => {
    messageHandler.confirm(
      'Excluir Campanha',
      `Tem certeza que deseja excluir a campanha "${campaign.name}"?`,
      () => {
        deleteMutation.mutate(campaign.id);
      }
    );
  };

  const handleSubmitFollow = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);

    const followData = {
      campaign_id: parseInt(formData.get('campaign_id') as string),
      stage: formData.get('stage') as string,
      interval_days: parseInt(formData.get('interval_days') as string),
      new_stage: formData.get('new_stage') as string,
      is_active: formData.get('is_active') === 'on',
      new_action: formData.get('new_action') as string || undefined,
      instructions: formData.get('instructions') as string || undefined,
      message_delay_minutes: parseInt(formData.get('message_delay_minutes') as string) || 0,
    };

    if (selectedFollowRule) {
      updateFollowMutation.mutate({ id: selectedFollowRule.id, data: followData });
    } else {
      createFollowMutation.mutate(followData);
    }
  };

  const handleDeleteFollow = (followRule: CampaignFollow) => {
    messageHandler.confirm(
      'Excluir Regra de Follow-up',
      `Tem certeza que deseja excluir esta regra de follow-up?`,
      () => {
        deleteFollowMutation.mutate(followRule.id);
      }
    );
  };

  const handleToggleFollowActive = (followRule: CampaignFollow) => {
    toggleFollowActiveMutation.mutate({
      id: followRule.id,
      is_active: !followRule.is_active,
    });
  };

  const handleFilterChange = (newFilters: Filters) => {
    setFilters(newFilters);
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setFilters({});
    setCurrentPage(1);
  };

  const handleFollowFilterChange = (newFilters: FollowFilters) => {
    setFollowFilters(newFilters);
    setFollowPage(1);
  };

  const handleClearFollowFilters = () => {
    setFollowFilters({});
    setFollowPage(1);
  };

  const campaigns = data?.data || [];
  const pagination = data?.pagination;
  const followRules = followData?.data || [];
  const followPagination = followData?.pagination;

  const tabs = [
    { id: 'campaigns', label: 'Campanhas' },
    { id: 'follows', label: 'Regras de Follow-up' },
  ];

  return (
    <div className="container mx-auto px-4 py-8">
      <PageHeader
        title="Campanhas de Marketing"
        description="Gerencie suas campanhas de marketing e regras de follow-up"
        actions={
          <Button onClick={() => activeTab === 'campaigns' ? handleOpenModal() : handleOpenFollowModal()}>
            <Plus className="w-4 h-4 mr-2" />
            {activeTab === 'campaigns' ? 'Nova Campanha' : 'Nova Regra de Follow-up'}
          </Button>
        }
      />

      <div className="space-y-6 mt-6">
        <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} />

        {activeTab === 'campaigns' ? (
          <>
            <CampaignFilters
              filters={filters}
              onFilterChange={handleFilterChange}
              onClearFilters={handleClearFilters}
            />

            {isLoading ? (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 dark:border-white"></div>
              </div>
            ) : error ? (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 text-center">
                <p className="text-red-800 dark:text-red-200">
                  Erro ao carregar campanhas. Por favor, tente novamente.
                </p>
              </div>
            ) : (
              <>
                <CampaignList
                  campaigns={campaigns}
                  onEdit={handleOpenModal}
                  onDelete={handleDelete}
                />

                {pagination && pagination.last_page > 1 && (
                  <Pagination
                    currentPage={pagination.current_page}
                    totalPages={pagination.last_page}
                    onPageChange={setCurrentPage}
                  />
                )}
              </>
            )}
          </>
        ) : (
          <>
            <CampaignFollowFilters
              filters={followFilters}
              onFilterChange={handleFollowFilterChange}
              onClearFilters={handleClearFollowFilters}
            />

            {followLoading ? (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 dark:border-white"></div>
              </div>
            ) : followError ? (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 text-center">
                <p className="text-red-800 dark:text-red-200">
                  Erro ao carregar regras de follow-up. Por favor, tente novamente.
                </p>
              </div>
            ) : (
              <>
                <CampaignFollowList
                  followRules={followRules}
                  onEdit={handleOpenFollowModal}
                  onDelete={handleDeleteFollow}
                  onToggleActive={handleToggleFollowActive}
                />

                {followPagination && followPagination.last_page > 1 && (
                  <Pagination
                    currentPage={followPagination.current_page}
                    totalPages={followPagination.last_page}
                    onPageChange={setFollowPage}
                  />
                )}
              </>
            )}
          </>
        )}
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={
          selectedCampaign
            ? 'Editar Campanha'
            : selectedFollowRule
            ? 'Editar Regra de Follow-up'
            : activeTab === 'campaigns'
            ? 'Nova Campanha'
            : 'Nova Regra de Follow-up'
        }
      >
        {selectedCampaign || (!selectedFollowRule && activeTab === 'campaigns') ? (
          <CampaignForm
            campaign={selectedCampaign}
            onSubmit={handleSubmit}
            isLoading={createMutation.isPending || updateMutation.isPending}
            onClose={handleCloseModal}
          />
        ) : (
          <CampaignFollowForm
            followRule={selectedFollowRule}
            onSubmit={handleSubmitFollow}
            isLoading={createFollowMutation.isPending || updateFollowMutation.isPending}
            onClose={handleCloseModal}
          />
        )}
      </Modal>
    </div>
  );
}

export default Campaigns;
